import React, { useState, useEffect } from "react";
import { API } from "../../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Sparkles, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function SubscribeDialog({ open, onClose }) {
  const [plans, setPlans] = useState(null);
  const [plan, setPlan] = useState("annual");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && !plans) {
      fetch(`${API}/billing/plans`)
        .then((r) => r.json())
        .then(setPlans)
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line

  const subscribe = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/billing/create-checkout-session`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ plan, origin_url: window.location.origin, visitor_session_token: localStorage.getItem("visitor_session_token") }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url; // redirect to Stripe Checkout
      } else {
        toast.error(data.detail || "Could not start checkout");
        setLoading(false);
      }
    } catch {
      toast.error("Network error");
      setLoading(false);
    }
  };

  const monthly = plans?.monthly;
  const annual = plans?.annual;
  const annualSaving =
    monthly && annual ? Math.round(monthly.amount * 12 - annual.amount) : 0;
  const monthsFree =
    monthly && annual && monthly.amount ? Math.round((monthly.amount * 12 - annual.amount) / monthly.amount) : 0;
  const annualPerMonth = annual ? (annual.amount / 12).toFixed(2) : null;

  const PlanCard = ({ p, value, badge, subtext, highlight }) => {
    if (!p) return null;
    const selected = plan === value;
    return (
      <button
        type="button"
        onClick={() => setPlan(value)}
        className={`w-full text-left rounded-xl border-2 p-4 transition-colors relative ${
          selected ? "border-amber-500 bg-amber-50" : highlight ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"
        }`}
        data-testid={`plan-${value}`}
      >
        {badge && (
          <span className="absolute -top-2 right-3 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm" data-testid={`plan-${value}-badge`}>
            {badge}
          </span>
        )}
        <div className="flex items-center justify-between">
          <span className="font-semibold text-slate-900 capitalize">{value}</span>
          {selected && <Check className="w-4 h-4 text-amber-600" />}
        </div>
        <div className="mt-1">
          <span className="text-2xl font-bold text-slate-900">${p.amount}</span>
          <span className="text-slate-500 text-sm"> {p.currency} / {value === "annual" ? "year" : "month"}</span>
        </div>
        {subtext ? (
          <p className="text-xs font-medium text-emerald-700 mt-1" data-testid={`plan-${value}-subtext`}>{subtext}</p>
        ) : (
          <p className="text-xs text-slate-400 mt-1">GST inclusive</p>
        )}
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="subscribe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" /> Subscribe to SiteProApp
          </DialogTitle>
          <DialogDescription>Unlimited sites · Unlimited users · Cancel anytime</DialogDescription>
        </DialogHeader>

        {!plans ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
        ) : (
          <div className="space-y-3">
            <PlanCard
              p={annual}
              value="annual"
              highlight={annualSaving > 0}
              badge={monthsFree > 0 ? `${monthsFree} MONTHS FREE` : (annualSaving > 0 ? `Save $${annualSaving}` : null)}
              subtext={annualSaving > 0 ? `Save $${annualSaving}/yr · just $${annualPerMonth}/mo billed yearly` : null}
            />
            <PlanCard p={monthly} value="monthly" />
            <Button
              onClick={subscribe}
              disabled={loading}
              className="w-full h-12 text-base bg-amber-500 hover:bg-amber-600 font-semibold"
              data-testid="subscribe-checkout-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue to Payment"}
            </Button>
            <p className="text-[11px] text-center text-slate-400">
              Secure payment via Stripe. You'll be redirected to complete your subscription.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function useSubscribeDialog() {
  const [open, setOpen] = useState(false);
  return { open, openDialog: () => setOpen(true), closeDialog: () => setOpen(false) };
}
